// Meta-Formate für Facebook & Instagram - basierend auf Briefing
// Alle Spezifikationen nach aktuellen Meta-Richtlinien

export type Platform = 'facebook' | 'instagram';
export type ContentType = 'organisch' | 'anzeige';
export type FormatCategory = 'bild' | 'video' | 'carousel' | 'collection';

export interface AspectRatioSpec {
  ratio: string;      // z.B. "1:1", "4:5", "9:16"
  width: number;
  height: number;
  name: string;
  nameKey?: string;   // i18n key for translation (e.g., "square", "portrait")
  placement: string;  // z.B. "Feed", "Story", "Reel"
  platforms?: ('facebook' | 'instagram')[];  // Unterstützte Plattformen (optional für Legacy)
  postTypes?: ('feed' | 'story' | 'reel' | 'carousel')[];  // Unterstützte Post-Typen (optional für Legacy)
}

export interface TextLimits {
  primText: number;
  headline?: number;
  beschreibung?: number;
  caption?: number;
  hashtags?: number;
}

export interface FormatSpec {
  format: string;
  aspectRatios: AspectRatioSpec[];
  empfohleneAufloesung: string;
  dateigroesseMax: string;
  laengeMax?: string;        // für Videos
  kartenAnzahl?: string;     // für Carousel
  textLimits?: TextLimits;
  besonderheiten?: string;
  verfuegbar?: boolean;
}

export interface MetaFormat {
  facebook: {
    organisch: FormatSpec;
    anzeige: FormatSpec;
  };
  instagram: {
    organisch: FormatSpec;
    anzeige: FormatSpec;
  };
}

// Vordefinierte Aspect Ratios mit Pixelwerten (Legacy - benutze CANVAS_PRESETS für neue Features)
export const ASPECT_RATIOS: Record<string, AspectRatioSpec[]> = {
  // Feed-Formate
  quadrat: [
    { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed', platforms: ['facebook', 'instagram'], postTypes: ['feed', 'carousel'] },
  ],
  hochformat: [
    { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed', platforms: ['facebook', 'instagram'], postTypes: ['feed', 'carousel'] },
  ],
  querformat: [
    { ratio: '1.91:1', width: 1200, height: 628, name: 'Querformat', placement: 'Feed', platforms: ['facebook'], postTypes: ['feed'] },
    { ratio: '16:9', width: 1920, height: 1080, name: 'Widescreen', placement: 'Feed', platforms: ['facebook'], postTypes: ['feed'] },
  ],
  // Vollbild-Formate
  story: [
    { ratio: '9:16', width: 1080, height: 1920, name: 'Story/Reel', placement: 'Story', platforms: ['facebook', 'instagram'], postTypes: ['story', 'reel'] },
  ],
};

// Alle unterstützten Canvas-Größen für den Creator
// Jedes Format zeigt an, für welche Plattformen (FB/IG) und Post-Typen es geeignet ist
export const CANVAS_PRESETS: AspectRatioSpec[] = [
  // Instagram/Facebook Feed
  {
    ratio: '1:1',
    width: 1080,
    height: 1080,
    name: 'Quadrat (Feed/Carousel)',
    nameKey: 'square',
    placement: 'Feed',
    platforms: ['facebook', 'instagram'],
    postTypes: ['feed', 'carousel'],
  },
  {
    ratio: '4:5',
    width: 1080,
    height: 1350,
    name: 'Hochformat (Feed)',
    nameKey: 'portrait',
    placement: 'Feed',
    platforms: ['facebook', 'instagram'],
    postTypes: ['feed', 'carousel'],
  },
  {
    ratio: '1.91:1',
    width: 1200,
    height: 628,
    name: 'Querformat (Link-Preview)',
    nameKey: 'landscape',
    placement: 'Feed',
    platforms: ['facebook'],
    postTypes: ['feed'],
  },
  // Stories & Reels
  {
    ratio: '9:16',
    width: 1080,
    height: 1920,
    name: 'Story/Reel',
    nameKey: 'storyReel',
    placement: 'Story',
    platforms: ['facebook', 'instagram'],
    postTypes: ['story', 'reel'],
  },
];

// Text-Limits nach Meta-Richtlinien
export const TEXT_LIMITS = {
  facebook: {
    organisch: {
      primText: null,  // Keine strikte Begrenzung, aber prägnant empfohlen
      sichtbar: 125,   // Sichtbar ohne "Mehr anzeigen"
    },
    anzeige: {
      primText: 125,
      headline: 27,
      beschreibung: 27,
    },
  },
  instagram: {
    organisch: {
      caption: 2200,
      sichtbar: 125,
      hashtags: 30,
    },
    anzeige: {
      primText: 125,
      headline: 40,
    },
  },
};

// Vollständige Meta-Formate Konfiguration
export const META_FORMATS: Record<FormatCategory, MetaFormat> = {
  bild: {
    facebook: {
      organisch: {
        format: 'JPG/PNG Bild',
        aspectRatios: [
          { ratio: '1.91:1', width: 1200, height: 628, name: 'Querformat', placement: 'Feed' },
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
        ],
        empfohleneAufloesung: '>=1080x1080 Pixel',
        dateigroesseMax: '30 MB',
        textLimits: {
          primText: 125, // sichtbar ohne "Mehr"
        },
        besonderheiten: 'Keine strikte Textbegrenzung; nur erste Zeilen sichtbar',
      },
      anzeige: {
        format: 'Single Image Ad',
        aspectRatios: [
          { ratio: '1.91:1', width: 1200, height: 628, name: 'Querformat', placement: 'Feed' },
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '9:16', width: 1080, height: 1920, name: 'Story', placement: 'Story' },
        ],
        empfohleneAufloesung: '>=1080x1080 (Feed); 1080x1920 (Story)',
        dateigroesseMax: '30 MB',
        textLimits: {
          primText: 125,
          headline: 27,
          beschreibung: 27,
        },
        besonderheiten: 'CTA-Button optional; in Story-Ads UI-Elemente beachten (14% oben, 20% unten)',
      },
    },
    instagram: {
      organisch: {
        format: 'JPG/PNG Bild',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed' },
          { ratio: '1.91:1', width: 1200, height: 628, name: 'Querformat', placement: 'Feed' },
        ],
        empfohleneAufloesung: '>=1080x1080 Pixel',
        dateigroesseMax: '30 MB',
        textLimits: {
          caption: 2200,
          primText: 125,
          hashtags: 30,
        },
        besonderheiten: 'Caption bis 2.200 Zeichen; nach ~125 Zeichen gekürzt im Feed',
      },
      anzeige: {
        format: 'Image Ad',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed' },
          { ratio: '9:16', width: 1080, height: 1920, name: 'Story', placement: 'Story' },
        ],
        empfohleneAufloesung: '>=1080x1080 (Feed); 1080x1920 (Story)',
        dateigroesseMax: '30 MB',
        textLimits: {
          primText: 125,
          headline: 40,
        },
        besonderheiten: 'CTA-Button unter dem Bild; mobile-only Placements',
      },
    },
  },
  video: {
    facebook: {
      organisch: {
        format: 'MP4/MOV Video',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed' },
          { ratio: '16:9', width: 1920, height: 1080, name: 'Querformat', placement: 'Feed' },
        ],
        empfohleneAufloesung: '>=1080p',
        dateigroesseMax: '4 GB',
        laengeMax: '240 Minuten',
        besonderheiten: 'Autoplay ohne Ton – Untertitel empfohlen',
      },
      anzeige: {
        format: 'Video Ad',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed' },
          { ratio: '9:16', width: 1080, height: 1920, name: 'Story/Reel', placement: 'Story' },
        ],
        empfohleneAufloesung: '>=1080p',
        dateigroesseMax: '4 GB',
        laengeMax: '241 Min (Feed); 120s (Story)',
        textLimits: {
          primText: 125,
          headline: 27,
          beschreibung: 27,
        },
        besonderheiten: 'H.264 Video, AAC Audio; erste Sekunden entscheidend',
      },
    },
    instagram: {
      organisch: {
        format: 'MP4/MOV Video',
        aspectRatios: [
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed' },
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '9:16', width: 1080, height: 1920, name: 'Story/Reel', placement: 'Story' },
        ],
        empfohleneAufloesung: '>=1080p',
        dateigroesseMax: '4 GB',
        laengeMax: '60 Min (Feed); 90s+ (Reels)',
        besonderheiten: 'Autoplay mit Ton; Reels loopen standardmäßig',
      },
      anzeige: {
        format: 'Video Ad',
        aspectRatios: [
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Feed' },
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Feed' },
          { ratio: '9:16', width: 1080, height: 1920, name: 'Story/Reel', placement: 'Story' },
        ],
        empfohleneAufloesung: '>=1080p',
        dateigroesseMax: '4 GB',
        laengeMax: '60 Min (Feed); 120s (Story); 15 Min (Reel)',
        textLimits: {
          primText: 125,
          headline: 40,
        },
        besonderheiten: 'Mobile Optimierung wichtig; Swipe-Up Link in Stories',
      },
    },
  },
  carousel: {
    facebook: {
      organisch: {
        format: 'Album/Multi-Bild',
        aspectRatios: [],
        empfohleneAufloesung: '',
        dateigroesseMax: '',
        verfuegbar: false,
        besonderheiten: 'Kein echtes Carousel organisch; nur Multi-Bild als Album',
      },
      anzeige: {
        format: 'Carousel Ad',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat (empfohlen)', placement: 'Carousel' },
        ],
        empfohleneAufloesung: '1080x1080 Pixel',
        dateigroesseMax: '30 MB (Bild), 4 GB (Video)',
        kartenAnzahl: '2-10 Karten',
        laengeMax: '120s pro Video-Karte',
        textLimits: {
          primText: 125,
          headline: 45,
        },
        besonderheiten: 'Eigener Link und Überschrift je Karte; ideal für Produkte/Storytelling',
      },
    },
    instagram: {
      organisch: {
        format: 'Carousel-Post',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Carousel' },
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Carousel' },
        ],
        empfohleneAufloesung: '1080x1080 oder 1080x1350',
        dateigroesseMax: '30 MB',
        kartenAnzahl: 'bis zu 10 Bilder/Videos',
        besonderheiten: 'Keine individuellen Links pro Bild; einheitliches Seitenverhältnis',
      },
      anzeige: {
        format: 'Carousel Ad',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Quadrat', placement: 'Carousel' },
          { ratio: '4:5', width: 1080, height: 1350, name: 'Hochformat', placement: 'Carousel' },
        ],
        empfohleneAufloesung: '1080x1080 oder 1080x1350',
        dateigroesseMax: '30 MB (Bild), 4 GB (Video)',
        kartenAnzahl: '2-10 Karten',
        laengeMax: '120s pro Video-Karte',
        textLimits: {
          primText: 125,
          headline: 40,
        },
        besonderheiten: 'Je Karte eigener Link/CTA',
      },
    },
  },
  collection: {
    facebook: {
      organisch: {
        format: 'Nicht verfügbar',
        aspectRatios: [],
        empfohleneAufloesung: '',
        dateigroesseMax: '',
        verfuegbar: false,
        besonderheiten: 'Kein organisches Format; nur als bezahlte Collection-Ad',
      },
      anzeige: {
        format: 'Collection Ad (mit Instant Experience)',
        aspectRatios: [
          { ratio: '1:1', width: 1080, height: 1080, name: 'Cover', placement: 'Collection' },
        ],
        empfohleneAufloesung: '1080x1080 Pixel',
        dateigroesseMax: '30 MB (Bild), 4 GB (Video)',
        textLimits: {
          primText: 125,
          headline: 40,
        },
        besonderheiten: 'Cover-Bild/-Video + 3-4 Produktbilder aus Katalog; Instant Experience erforderlich',
      },
    },
    instagram: {
      organisch: {
        format: 'Nicht verfügbar',
        aspectRatios: [],
        empfohleneAufloesung: '',
        dateigroesseMax: '',
        verfuegbar: false,
        besonderheiten: 'Kein organisches Format; Instagram-Shops im Shop-Bereich',
      },
      anzeige: {
        format: 'Collection Ad',
        aspectRatios: [
          { ratio: '1.91:1', width: 1200, height: 628, name: 'Cover Querformat', placement: 'Collection' },
          { ratio: '1:1', width: 1080, height: 1080, name: 'Cover Quadrat', placement: 'Collection' },
        ],
        empfohleneAufloesung: '>=1080px Breite',
        dateigroesseMax: '30 MB (Bild), 4 GB (Video)',
        textLimits: {
          primText: 125,
          headline: 40,
        },
        besonderheiten: '3-4 Produktbilder aus Katalog; Instant Experience beim Tap',
      },
    },
  },
};

// Sichere Bereiche für Story-Format (UI-Elemente überlagern Ränder)
export const STORY_SAFE_ZONES = {
  topMarginPercent: 14,    // ~14% oben für Profilinfo etc.
  bottomMarginPercent: 20, // ~20% unten für CTA, Swipe-Up etc.
};

// Hilfsfunktion: Canvas-Größe für Format ermitteln
export function getCanvasSize(
  category: FormatCategory,
  platform: Platform,
  placement: string = 'Feed'
): { width: number; height: number } {
  const format = META_FORMATS[category]?.[platform]?.organisch;
  if (!format?.aspectRatios) {
    return { width: 1080, height: 1080 }; // Default: Quadrat
  }

  const aspectRatio = format.aspectRatios.find(ar => ar.placement === placement);
  if (aspectRatio) {
    return { width: aspectRatio.width, height: aspectRatio.height };
  }

  // Fallback: erste verfügbare
  if (format.aspectRatios.length > 0) {
    return { width: format.aspectRatios[0].width, height: format.aspectRatios[0].height };
  }

  return { width: 1080, height: 1080 };
}

// Hilfsfunktion: Text-Limits für Format ermitteln
export function getTextLimits(
  category: FormatCategory,
  platform: Platform,
  contentType: ContentType
): TextLimits | null {
  return META_FORMATS[category]?.[platform]?.[contentType]?.textLimits || null;
}

// Hilfsfunktion: Plattform-Label für AspectRatioSpec generieren
export function getPlatformLabel(preset: AspectRatioSpec): string {
  if (!preset.platforms || preset.platforms.length === 0) {
    return '';
  }
  if (preset.platforms.length === 2) {
    return 'FB + IG';
  }
  return preset.platforms[0] === 'facebook' ? 'FB' : 'IG';
}

// Hilfsfunktion: Post-Typen Label für AspectRatioSpec generieren
export function getPostTypesLabel(preset: AspectRatioSpec): string {
  if (!preset.postTypes || preset.postTypes.length === 0) {
    return '';
  }
  const labels: Record<string, string> = {
    feed: 'Feed',
    story: 'Story',
    reel: 'Reel',
    carousel: 'Carousel',
  };
  return preset.postTypes.map(t => labels[t] || t).join(', ');
}
